import { Fragment } from 'react'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { Box } from '@mui/material'
import { Attribute, AttributeLabel, AttributeValue } from 'packages/eid-ui'
import { ResourceOwnersList } from '../../../../components/Owners/ResourceOwnersList'
import { useAccessRequestPolicy } from 'hooks'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@mui/material'
import DynamicComponents from 'components/DynamicComponents'
import { ResponsibleParty } from 'components'
import useSubcomponents from 'useSubcomponents'

const smallScreenWidth = 960

const canSeeApproversControlName =
    'ResourceAdmin-SharedFolder-Overview-Owners-Control'

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            position: 'relative',
            padding: theme.spacing(2),
            display: 'flex',
            miHeight: '156px',
            justifyContent: 'space-between',

            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                flexDirection: 'column',
            },
        },
        content: {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',

            [`@media (max-width:345px)`]: {
                flexDirection: 'column-reverse',
            },
            [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                display: 'block',
            },
        },
    }),
)

export const MainOverview = (props) => {
    const classes = useStyles()

    const { hasAccessToControl } = useSubcomponents()
    const { basicAttributes, detailsLoading, sharedFolder } = props
    const policy = useAccessRequestPolicy(
        sharedFolder && sharedFolder.requestPolicyId,
    )

    const { t } = useTranslation()

    const renderComponent = (a, item) => {
        const DynamicComponentToRender =
            DynamicComponents[a.component.componentName]
        return <DynamicComponentToRender attribute={a} data={{ item }} />
    }

    const sharedFolderDetails = (
        <Box>
            {basicAttributes &&
                basicAttributes
                    .filter((att) => att.firstPane)
                    .map((att, i) => {
                        return (
                            <Attribute key={att.value + i}>
                                <AttributeLabel>{t(att.label)}</AttributeLabel>
                                <AttributeValue>
                                    {att.component &&
                                        renderComponent(att, sharedFolder)}
                                    {sharedFolder &&
                                        !att.hideValue &&
                                        (sharedFolder[att.value] ?? '-')}
                                </AttributeValue>
                            </Attribute>
                        )
                    })}
        </Box>
    )
    const policyDetails = (
        <Box>
            <Attribute>
                <AttributeLabel>{t('PublishInIAMShop')}</AttributeLabel>
                <AttributeValue>
                    {sharedFolder?.requestable ? t('Yes') : t('No')}
                </AttributeValue>
            </Attribute>
            <Attribute>
                <AttributeLabel>{t('AccessRequestPolicy')}</AttributeLabel>
                <AttributeValue>{policy && policy.name}</AttributeValue>
            </Attribute>
            {basicAttributes &&
                basicAttributes
                    .filter((att) => !att.firstPane)
                    .map((att, i) => {
                        return (
                            <Attribute key={att.value + i}>
                                <AttributeLabel>{t(att.label)}</AttributeLabel>
                                <AttributeValue>
                                    {att.component &&
                                        renderComponent(att, sharedFolder)}
                                    {sharedFolder &&
                                        !att.hideValue &&
                                        (sharedFolder[att.value] ?? '-')}
                                </AttributeValue>
                            </Attribute>
                        )
                    })}
        </Box>
    )
    const showApplicationDetails = hasAccessToControl(
        'ResourceAdmin-SharedFolder-Overview-Details-Control',
    )
    const showPolicyDetails = hasAccessToControl(
        'ResourceAdmin-SharedFolder-Overview-Policies-Details-Control',
    )

    const showResponsiblePartyDetails = hasAccessToControl(
        'ResourceAdmin-SharedFolder-Overview-ResponsibleParty-Control',
    )

    const renderContent = () => (
        <Box className={classes.content}>
            {showApplicationDetails && <Box>{sharedFolderDetails}</Box>}

            <Box>{showPolicyDetails ? policyDetails : null}</Box>

            <Box>
                {hasAccessToControl(canSeeApproversControlName) && (
                    <Box
                        data-protectedsubcomponent={canSeeApproversControlName}
                    >
                        <ResourceOwnersList
                            resourceId={sharedFolder?.id}
                            label={'SharedFolders_Owners'}
                            contextKey="RequestViewSharedFoldersOwners"
                        />
                    </Box>
                )}
                {showResponsiblePartyDetails && (
                    <>
                        {sharedFolder?.ownerIsPerson ? (
                            <ResponsibleParty
                                responsiblePersonId={sharedFolder?.ownerId}
                            />
                        ) : (
                            <Attribute>
                                <AttributeLabel>
                                    {t('ResponsibleParty')}
                                </AttributeLabel>
                                <AttributeValue>
                                    {sharedFolder?.ownerFriendlyName || '-'}
                                </AttributeValue>
                            </Attribute>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )

    return (
        <>
            {detailsLoading ? (
                <Box width={'100%'}>
                    <Skeleton height={40} />
                </Box>
            ) : (
                <Fragment>
                    <Box>{renderContent()}</Box>
                </Fragment>
            )}
        </>
    )
}
